'use client';

import React from 'react';
import { Lock, Sparkles } from 'lucide-react';

interface RevealHeroProps {
  revealed: boolean;
  winner: 'boy' | 'girl' | 'tie';
  boyVotes: number;
  girlVotes: number;
  totalVotes: number;
  event: {
    parentName1: string;
    parentName2: string;
    eventTitle: string;
    babyNameHint: string;
  };
  onReveal: () => void;
}

export default function RevealHero({
  revealed,
  winner,
  boyVotes,
  girlVotes,
  totalVotes,
  event,
  onReveal,
}: RevealHeroProps) {
  if (!revealed) {
    return (
      <div className="bg-card rounded-3xl border border-border card-shadow overflow-hidden">
        {/* Hidden state */}
        <div className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50 p-8 text-center min-h-[280px] flex flex-col items-center justify-center">
          {/* Blurred preview */}
          <div className="absolute inset-0 flex items-center justify-center opacity-10 select-none pointer-events-none">
            <span className="text-9xl">💙🩷</span>
          </div>

          <div className="relative z-10">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center mx-auto mb-4 card-shadow">
              <Lock size={28} className="text-purple-500" />
            </div>
            <h2 className="text-2xl font-800 text-foreground mb-2">
              Resultados Ocultos
            </h2>
            <p className="text-muted-foreground text-sm mb-2 max-w-xs mx-auto">
              {totalVotes} personas han votado. ¿Están listos para la gran revelación?
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              {event.parentName1} & {event.parentName2}
            </p>

            <button
              onClick={onReveal}
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white font-700 text-lg card-shadow hover:opacity-90 active:scale-95 transition-all duration-150 pulse-ring relative"
            >
              <Sparkles size={20} />
              ¡Revelar Resultado!
              <Sparkles size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Revealed state
  const isGirl = winner === 'girl';
  const isTie = winner === 'tie';

  const winnerConfig = isTie
    ? {
        gradient: 'from-blue-400 via-purple-400 to-pink-400',
        glow: '',
        emoji: '💙🩷',
        label: '¡Empate!',
        sublabel: 'La mitad vota niño, la mitad niña',
        bg: 'from-blue-50 via-purple-50 to-pink-50',
        pct: 50,
        loserLabel: '',
        loserPct: 50,
      }
    : isGirl
    ? {
        gradient: 'bg-girl-gradient',
        glow: 'winner-glow-pink',
        emoji: '🩷👶🌸✨',
        label: '¡Es una Niña!',
        sublabel: `${girlVotes} de ${totalVotes} personas lo predijeron`,
        bg: 'from-pink-50 via-rose-50 to-fuchsia-50',
        pct: Math.round((girlVotes / totalVotes) * 100),
        loserLabel: `${boyVotes} votaron niño`,
        loserPct: Math.round((boyVotes / totalVotes) * 100),
      }
    : {
        gradient: 'bg-boy-gradient',
        glow: 'winner-glow-blue',
        emoji: '💙👶⭐✨',
        label: '¡Es un Niño!',
        sublabel: `${boyVotes} de ${totalVotes} personas lo predijeron`,
        bg: 'from-blue-50 via-sky-50 to-cyan-50',
        pct: Math.round((boyVotes / totalVotes) * 100),
        loserLabel: `${girlVotes} votaron niña`,
        loserPct: Math.round((girlVotes / totalVotes) * 100),
      };

  return (
    <div
      className={`bg-gradient-to-br ${winnerConfig.bg} rounded-3xl border border-border overflow-hidden ${winnerConfig.glow}`}
    >
      <div className="p-8 text-center bounce-in">
        {/* Emoji burst */}
        <div className="text-5xl mb-4 float-animation">{winnerConfig.emoji}</div>

        {/* Winner badge */}
        <div
          className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full ${
            isTie
              ? 'bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400'
              : winnerConfig.gradient
          } text-white font-800 text-2xl mb-3 ${winnerConfig.glow}`}
        >
          {winnerConfig.label}
        </div>

        <p className="text-muted-foreground font-500 text-sm mb-1">{winnerConfig.sublabel}</p>

        {event.babyNameHint && (
          <p className="text-xs text-muted-foreground italic mt-1">
            Pista del nombre: {event.babyNameHint}
          </p>
        )}

        {/* Parents */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            Revelación de{' '}
            <span className="font-700 text-foreground">
              {event.parentName1} & {event.parentName2}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}